// ============================================================
// DockerExamination.jsx
// ============================================================
// Junior-dev notes:
// - Security: Detects Tab Switching and Window Blur.
// - UI: Premium Gold (#E5C95F) on Neural Purple (#100720).
// - Logic: Randomizes 30 questions from the Docker Expert Bank.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    Paper,
    Stack,
    Button,
    IconButton,
    LinearProgress,
    Alert,
    Snackbar,
    Grid,
    RadioGroup,
    FormControlLabel as MUIFormControlLabel,
    Radio,
    TextField,
    MenuItem,
    Avatar,
} from "@mui/material";

/* Icons */
import TerminalIcon from "@mui/icons-material/Terminal";
import TimerIcon from "@mui/icons-material/Timer";
import GavelIcon from "@mui/icons-material/Gavel";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { FaVolumeUp, FaVolumeMute, FaShieldAlt, FaDocker } from "react-icons/fa";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(229, 201, 95, 0.2)",
    gold: "#E5C95F",
    goldBright: "#FFF0A5",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    error: "#FF5252",
    inputBg: "rgba(255,255,255,0.05)",
};

const EXAM_CONFIG = {
    totalQuestions: 30,
    durationSeconds: 25 * 60, // 25 Minutes for Docker
    maxViolations: 3,
};

/* ── Docker Expert Question Bank: Full 30-Question Array ── */
const DOCKER_BANK = [
    // 1-5: Core Architecture & CLI
    { id: "D1", topic: "Layers", question: "Which command instruction creates a new read-only layer in the image?", options: ["CMD", "RUN", "ENV", "EXPOSE"], answerIndex: 1 },
    { id: "D2", topic: "Storage", question: "Which type of mount is managed entirely by the Docker Host filesystem?", options: ["Bind Mount", "tmpfs Mount", "Named Volume", "NFS Mount"], answerIndex: 2 },
    { id: "D3", topic: "Networking", question: "What is the default network driver for standalone containers?", options: ["Host", "Overlay", "None", "Bridge"], answerIndex: 3 },
    { id: "D4", topic: "Optimization", question: "Which base image is most commonly used to minimize final image size?", options: ["Ubuntu", "CentOS", "Alpine", "Debian"], answerIndex: 2 },
    { id: "D5", topic: "Compose", question: "Which command starts containers in the background?", options: ["docker-compose start", "docker-compose up -d", "docker-compose run", "docker-compose build"], answerIndex: 1 },

    // 6-10: Networking & Connectivity
    { id: "D6", topic: "Networking", question: "In a user-defined bridge network, how do containers find each other?", options: ["By MAC address", "By Container Name (DNS)", "By random Port", "By Host IP"], answerIndex: 1 },
    { id: "D7", topic: "Ports", question: "In the command 'docker run -p 8080:3000', what does 8080 represent?", options: ["The Container Port", "The Host Port", "The Internal IP", "The Bridge Gateway"], answerIndex: 1 },
    { id: "D8", topic: "Networking", question: "Which driver allows a container to share the host's IP address directly?", options: ["Bridge", "Macvlan", "Host", "Overlay"], answerIndex: 2 },
    { id: "D9", topic: "Service Discovery", question: "What happens when you use 'links' in modern Docker Compose?", options: ["It is the preferred method", "It is deprecated in favor of networks", "It improves security", "It is required for DBs"], answerIndex: 1 },
    { id: "D10", topic: "Networking", question: "Which command lists all networks on the current host?", options: ["docker network ls", "docker net show", "docker-compose ps", "docker network inspect"], answerIndex: 0 },

    // 11-15: Dockerfile & Optimization
    { id: "D11", topic: "Optimization", question: "What is the primary benefit of Multi-stage builds?", options: ["Faster container startup", "Reduced final image size", "Easier debugging", "Automatic scaling"], answerIndex: 1 },
    { id: "D12", topic: "Dockerfile", question: "Which instruction specifies the command that always runs when the container starts?", options: ["RUN", "CMD", "ENTRYPOINT", "START"], answerIndex: 2 },
    { id: "D13", topic: "Caching", question: "To optimize the build cache, what should you copy first?", options: ["Source Code", "Images", "Dependencies (package.json/pom.xml)", "Environment Variables"], answerIndex: 2 },
    { id: "D14", topic: "Context", question: "Which file prevents unnecessary local files from being sent to the Docker daemon?", options: [".env", ".dockerignore", ".gitignore", "docker-compose.yml"], answerIndex: 1 },
    { id: "D15", topic: "Dockerfile", question: "What is the difference between ADD and COPY?", options: ["No difference", "ADD can fetch remote URLs and extract tarballs", "COPY is faster", "ADD is for metadata only"], answerIndex: 1 },

    // 16-20: Storage & Persistence
    { id: "D16", topic: "Persistence", question: "Where does Docker store Named Volumes on a Linux host by default?", options: ["/etc/docker", "/home/user/volumes", "/var/lib/docker/volumes", "/tmp/docker"], answerIndex: 2 },
    { id: "D17", topic: "Storage", question: "What happens to data in a volume when the container is deleted?", options: ["It is deleted", "It is moved to the host", "It persists until explicitly removed", "It is compressed"], answerIndex: 2 },
    { id: "D18", topic: "Storage", question: "Which mount type is best for sharing source code during development?", options: ["Volume", "Bind Mount", "tmpfs", "S3 Bucket"], answerIndex: 1 },
    { id: "D19", topic: "Persistence", question: "Can multiple containers share the same volume simultaneously?", options: ["No, it's locked", "Yes, in read-only mode only", "Yes, for both reading and writing", "Only in Docker Swarm"], answerIndex: 2 },
    { id: "D20", topic: "Storage", question: "What is a 'dangling' volume?", options: ["A volume used by many containers", "A volume not connected to any container", "A corrupted volume", "A volume in the cloud"], answerIndex: 1 },

    // 21-25: Orchestration & Compose
    { id: "D21", topic: "Orchestration", question: "In Kubernetes, what is the smallest deployable unit?", options: ["Container", "Pod", "Service", "Node"], answerIndex: 1 },
    { id: "D22", topic: "Orchestration", question: "What does 'Self-healing' mean in orchestration?", options: ["App updates itself", "Orchestrator restarts failed containers", "The host reboots", "Docker Hub repairs images"], answerIndex: 1 },
    { id: "D23", topic: "Compose", question: "Which flag scales a service to 5 replicas in Docker Compose?", options: ["--replicas 5", "--scale web=5", "--count 5", "-d 5"], answerIndex: 1 },
    { id: "D24", topic: "Orchestration", question: "Which tool is Docker's native orchestration engine?", options: ["Kubernetes", "Swarm", "Nomad", "Mesos"], answerIndex: 1 },
    { id: "D25", topic: "Compose", question: "How does Compose handle environment variables by default?", options: ["Looks for .env file", "Requires manual input", "Reads from GitHub", "Asks the user"], answerIndex: 0 },

    // 26-30: Security & Best Practices
    { id: "D26", topic: "Security", question: "What is the security risk of running as 'root' inside a container?", options: ["Faster CPU usage", "Potential for container breakout to Host", "Port conflicts", "Image bloat"], answerIndex: 1 },
    { id: "D27", topic: "Security", question: "Which command scans an image for known vulnerabilities?", options: ["docker check", "docker scan (or snyk test)", "docker inspect", "docker top"], answerIndex: 1 },
    { id: "D28", topic: "Best Practice", question: "What should you never include in a Docker Image?", options: ["Configuration files", "Passwords/Secrets", "Standard Libraries", "Static Assets"], answerIndex: 1 },
    { id: "D29", topic: "Security", question: "Which flag prevents a container from writing to its own filesystem?", options: ["--lock", "--no-write", "--read-only", "--secure"], answerIndex: 2 },
    { id: "D30", topic: "Security", question: "What are 'Distroless' images?", options: ["Images with no OS kernel", "Images with only the app and its dependencies", "Images with no GUI", "Broken images"], answerIndex: 1 }
];
export default function DockerExamination() {
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [started, setStarted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(EXAM_CONFIG.durationSeconds);
    const timerRef = useRef(null);

    const [formData, setFormData] = useState({
        fullName: "",
        track: "DevOps Engineer",
        date: new Date().toISOString().split("T")[0],
        attemptNumber: 1,
    });

    const [violations, setViolations] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [results, setResults] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: "" });

    const current = questions[activeIndex];
    const progress = Math.round(((activeIndex + 1) / EXAM_CONFIG.totalQuestions) * 100);

    const speak = useCallback((text) => {
        if (!voiceEnabled || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    }, [voiceEnabled]);

    const addViolation = useCallback((type) => {
        setViolations((prev) => {
            const next = [...prev, { type, at: new Date().toLocaleTimeString() }];
            setSnack({ open: true, message: `Security Alert: ${type}` });
            speak(`Warning. ${type} detected. Violation ${next.length}.`);
            if (next.length >= EXAM_CONFIG.maxViolations) handleSubmit("Auto-Submit: Integrity Failure");
            return next;
        });
    }, [speak]);

    // Anti-Cheat: Visibility & Focus
    useEffect(() => {
        if (!started || submitted) return;
        const onVisibility = () => { if (document.visibilityState !== "visible") addViolation("Tab Switch"); };
        const onBlur = () => addViolation("Window Focus Lost");
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("blur", onBlur);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("blur", onBlur);
        };
    }, [started, submitted, addViolation]);

    const handleStart = async () => {
        const q = [...DOCKER_BANK].sort(() => 0.5 - Math.random()).slice(0, 30);
        setQuestions(q);
        setAnswers(new Array(q.length).fill(null));
        setStarted(true);
        speak(`Docker Professional exam started. Stay focused.`);

        timerRef.current = setInterval(() => setTimeLeft((t) => t - 1), 1000);
        try { if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen(); } catch {}
    };

    const handleSubmit = useCallback((reason = "Candidate Submission") => {
        if (timerRef.current) clearInterval(timerRef.current);
        const score = questions.reduce((acc, q, i) => acc + (answers[i] === q.answerIndex ? 1 : 0), 0);
        const res = {
            percent: Math.round((score / questions.length) * 100),
            correct: score,
            total: questions.length,
            violations: violations.length,
            reason
        };
        setResults(res);
        setSubmitted(true);
        setStarted(false);
        speak(`Exam finalized. Your score is ${res.percent} percent.`);
        if (document.fullscreenElement) document.exitFullscreen();
    }, [questions, answers, violations]);

    useEffect(() => { if (started && timeLeft <= 0) handleSubmit("Timer Elapsed"); }, [timeLeft, started, handleSubmit]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 8, px: 2, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* Header Navigation */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 6 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-1px" }}>
                            DOCKER <span style={{ color: BRAND.gold }}>PRO-CERT</span>
                        </Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted, textTransform: 'uppercase', letterSpacing: 2 }}>
                            Finnish Software Standards · Level 2
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setVoiceEnabled(!voiceEnabled)} sx={{ border: `1px solid ${BRAND.border}`, bgcolor: BRAND.surface }}>
                        {voiceEnabled ? <FaVolumeUp color={BRAND.gold} /> : <FaVolumeMute color={BRAND.gold} />}
                    </IconButton>
                </Stack>

                {/* 1. REGISTRATION PHASE */}
                {!started && !submitted && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={7}>
                            <Paper sx={{ p: 5, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                                    <AssignmentIndIcon sx={{ color: BRAND.gold }} />
                                    <Typography variant="h6">Candidate Identity</Typography>
                                </Stack>
                                <Stack spacing={3}>
                                    <TextField fullWidth label="Full Name" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} />
                                    <TextField select fullWidth label="Specialization" value={formData.track} onChange={(e) => setFormData({...formData, track: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }}>
                                        <MenuItem value="DevOps Engineer">DevOps Engineer</MenuItem>
                                        <MenuItem value="Full Stack Architect">Full Stack Architect</MenuItem>
                                        <MenuItem value="Cloud Systems Engineer">Cloud Systems Engineer</MenuItem>
                                    </TextField>
                                </Stack>
                                <Button fullWidth disabled={!formData.fullName} onClick={handleStart} variant="contained" sx={{ mt: 5, py: 2.5, bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900, "&:hover": { bgcolor: BRAND.goldBright } }}>
                                    Start Secure Session
                                </Button>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Paper sx={{ p: 5, bgcolor: "rgba(0,0,0,0.2)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Typography variant="h6" sx={{ color: BRAND.gold, mb: 3 }}>Integrity Protocol</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 2.2 }}>
                                    • Full-Screen Mode Required<br/>
                                    • Tab Switching Disabled<br/>
                                    • Real-time Biometric Tracking (Simulated)<br/>
                                    • 70% Minimum Passing Score
                                </Typography>
                                <Box sx={{ mt: 5, textAlign: 'center', opacity: 0.2 }}>
                                    <FaShieldAlt size={80} color={BRAND.gold} />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* 2. ACTIVE EXAMINATION PHASE */}
                {started && current && (
                    <Stack spacing={4}>
                        <Paper sx={{ p: 3, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="button" sx={{ color: BRAND.gold }}>Question {activeIndex + 1} of 30</Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TimerIcon sx={{ color: BRAND.gold, fontSize: 20 }} />
                                    <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                                        {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <LinearProgress variant="determinate" value={progress} sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.05)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.gold } }} />
                        </Paper>

                        <Paper sx={{ p: 5, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                            <Typography variant="h5" sx={{ mb: 5, fontWeight: 600 }}>{current.question}</Typography>
                            <RadioGroup value={answers[activeIndex] ?? ""} onChange={(e) => {
                                const next = [...answers];
                                next[activeIndex] = Number(e.target.value);
                                setAnswers(next);
                            }}>
                                {current.options.map((opt, i) => (
                                    <MUIFormControlLabel key={i} value={i} control={<Radio sx={{ color: BRAND.gold }} />} label={<Typography sx={{ py: 1 }}>{opt}</Typography>} sx={{ p: 2, my: 1, border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 2, "&:hover": { bgcolor: "rgba(255,255,255,0.03)" } }} />
                                ))}
                            </RadioGroup>
                            <Stack direction="row" spacing={2} sx={{ mt: 6 }}>
                                <Button variant="outlined" disabled={activeIndex === 0} onClick={() => setActiveIndex(prev => prev - 1)} sx={{ color: BRAND.gold, borderColor: BRAND.gold }}>Back</Button>
                                {activeIndex < 29 ? (
                                    <Button variant="contained" onClick={() => setActiveIndex(prev => prev + 1)} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800 }}>Next Question</Button>
                                ) : (
                                    <Button variant="contained" onClick={() => handleSubmit()} sx={{ bgcolor: BRAND.goldBright, color: BRAND.bg, fontWeight: 900 }}>Submit Final Batch</Button>
                                )}
                            </Stack>
                        </Paper>

                        <Alert severity="warning" sx={{ bgcolor: "transparent", border: `1px solid ${BRAND.error}`, color: BRAND.error }}>
                            Integrity Monitoring Active. Violations: {violations.length}/{EXAM_CONFIG.maxViolations}
                        </Alert>
                    </Stack>
                )}

                {/* 3. FINAL TRANSCRIPT PHASE */}
                {submitted && results && (
                    <Paper sx={{ p: 8, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 6, textAlign: 'center' }}>
                        <Avatar sx={{ bgcolor: BRAND.gold, width: 90, height: 90, mx: "auto", mb: 4 }}>
                            <FaDocker size={50} color={BRAND.bg} />
                        </Avatar>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>{results.percent}%</Typography>
                        <Typography variant="h6" sx={{ color: BRAND.gold, letterSpacing: 3, mb: 6 }}>CERTIFICATION STATUS: {results.percent >= 70 ? "PASSED" : "FAILED"}</Typography>

                        <Grid container spacing={3} sx={{ textAlign: 'left', p: 4, bgcolor: "rgba(0,0,0,0.4)", borderRadius: 3 }}>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: BRAND.muted }}>Candidate</Typography><Typography>{formData.fullName}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: BRAND.muted }}>Track</Typography><Typography>{formData.track}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: BRAND.muted }}>Correct Responses</Typography><Typography>{results.correct} / 30</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: BRAND.muted }}>Security Violations</Typography><Typography sx={{ color: results.violations > 0 ? BRAND.error : BRAND.gold }}>{results.violations}</Typography></Grid>
                        </Grid>

                        <Button fullWidth variant="contained" sx={{ mt: 6, py: 2, bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }} onClick={() => window.location.reload()}>
                            Finalize and Exit
                        </Button>
                    </Paper>
                )}

                <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({...snack, open: false})}>
                    <Alert severity="error" variant="filled" sx={{ width: '100%' }}>{snack.message}</Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}
